import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, RefreshCw, AlertCircle, CheckCircle, Check, X, Trash2, Send, Archive,
  Plus, MessageSquare, User, Reply, Edit2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const FeedbackTab = ({
  reviewStats,
  feedbackLoading,
  pendingReviews,
  approvedReviews,
  handleApproveReview,
  handleRejectReview,
  handleToggleReviewFeatured,
  handleDeleteReview,
  fetchAllData
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [verbalFeedback, setVerbalFeedback] = useState({
    customer_name: '',
    customer_phone: '',
    worker_name: '',
    rating: 5,
    comment: '',
    source: 'verbal'
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Reply state
  const [replyModal, setReplyModal] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  // Handle owner reply to review
  const handleReplyToReview = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    setReplying(true);
    try {
      await api.post(`/reviews/${replyModal.id}/respond`, { response: replyText });
      toast.success('Response added successfully');
      setReplyModal(null);
      setReplyText('');
      if (fetchAllData) fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add response');
    } finally {
      setReplying(false);
    }
  };

  // Delete owner response
  const handleDeleteResponse = async (reviewId) => {
    if (!window.confirm('Delete your response to this review?')) return;
    try {
      await api.delete(`/reviews/${reviewId}/respond`);
      toast.success('Response deleted');
      if (fetchAllData) fetchAllData();
    } catch (error) {
      toast.error('Failed to delete response');
    }
  };

  // Add verbal feedback
  const handleAddVerbalFeedback = async () => {
    if (!verbalFeedback.customer_name || !verbalFeedback.comment) {
      toast.error('Please enter customer name and feedback');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/reviews/verbal', verbalFeedback);
      toast.success('Verbal feedback added successfully');
      setShowAddModal(false);
      setVerbalFeedback({
        customer_name: '',
        customer_phone: '',
        worker_name: '',
        rating: 5,
        comment: '',
        source: 'verbal'
      });
      if (fetchAllData) fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add feedback');
    } finally {
      setSubmitting(false);
    }
  };

  // Notify worker about their feedback
  const handleNotifyWorker = async (review) => {
    if (!review.worker_id) {
      toast.error('No worker associated with this review');
      return;
    }
    try {
      await api.post(`/reviews/${review.id}/notify-worker`);
      toast.success(`Notification sent to ${review.worker_name || 'worker'}`);
      if (fetchAllData) fetchAllData();
    } catch (error) {
      toast.error('Failed to notify worker');
    }
  };

  // Archive a review
  const handleArchiveReview = async (reviewId) => {
    if (!window.confirm('Archive this review? It will be moved to the archive section.')) return;
    try {
      await api.post(`/reviews/${reviewId}/archive`);
      toast.success('Review archived');
      if (fetchAllData) fetchAllData();
    } catch (error) {
      toast.error('Failed to archive review');
    }
  };

  // Local renderStars function
  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={`star-${i}`} 
        className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="feedback-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Customer Feedback</h1>
          <p className="text-[var(--text-muted)] mt-1">Reviews and ratings from customers</p>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
          data-testid="add-verbal-feedback-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Verbal Feedback
        </Button>
      </div>

      {/* Add Verbal Feedback Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.95 }} 
              animate={{ scale: 1 }}
              className="bg-[var(--background-paper)] rounded-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[var(--primary)]" />
                  Add Verbal Feedback
                </h3>
                <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-sm text-[var(--text-muted)] mb-4">
                Record feedback received by phone, in-person, or other verbal communication.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Customer Name *</label>
                  <Input
                    value={verbalFeedback.customer_name}
                    onChange={(e) => setVerbalFeedback({...verbalFeedback, customer_name: e.target.value})}
                    placeholder="Enter customer name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Customer Phone (Optional)</label>
                  <Input
                    value={verbalFeedback.customer_phone}
                    onChange={(e) => setVerbalFeedback({...verbalFeedback, customer_phone: e.target.value})}
                    placeholder="555-555-5555"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Worker Name (if applicable)</label>
                  <Input
                    value={verbalFeedback.worker_name}
                    onChange={(e) => setVerbalFeedback({...verbalFeedback, worker_name: e.target.value})}
                    placeholder="Enter worker name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Rating</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        onClick={() => setVerbalFeedback({...verbalFeedback, rating: star})}
                        className="p-1 hover:scale-110 transition-transform"
                      >
                        <Star className={`w-6 h-6 ${star <= verbalFeedback.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Feedback Source</label>
                  <select
                    value={verbalFeedback.source}
                    onChange={(e) => setVerbalFeedback({...verbalFeedback, source: e.target.value})}
                    className="w-full p-2 border rounded-lg bg-[var(--background-paper)]"
                  >
                    <option value="verbal">Verbal (In-Person)</option>
                    <option value="phone">Phone Call</option>
                    <option value="text">Text Message</option>
                    <option value="email">Email</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Feedback/Comments *</label>
                  <textarea
                    value={verbalFeedback.comment}
                    onChange={(e) => setVerbalFeedback({...verbalFeedback, comment: e.target.value})}
                    placeholder="Enter the customer's feedback..."
                    rows={4}
                    className="w-full p-3 border rounded-lg bg-[var(--background-paper)]"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="btn-primary flex-1"
                  onClick={handleAddVerbalFeedback}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Feedback'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reply to Review Modal */}
      <AnimatePresence>
        {replyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setReplyModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className="bg-[var(--background-paper)] rounded-xl max-w-lg w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Reply className="w-5 h-5 text-[var(--primary)]" />
                  {replyModal.owner_response ? 'Edit Response' : 'Reply to Review'}
                </h3>
                <button onClick={() => setReplyModal(null)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Original Review */}
              <div className="bg-[var(--background-subtle)] p-4 rounded-lg mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium">{replyModal.customer_name}</p>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${star <= replyModal.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                </div>
                {replyModal.comment && (
                  <p className="text-sm text-[var(--text-secondary)] italic">&ldquo;{replyModal.comment}&rdquo;</p>
                )}
              </div>

              {/* Response Input */}
              <div>
                <label className="block text-sm font-medium mb-2">Your Public Response</label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Thank you for your feedback..."
                  rows={4}
                  className="w-full p-3 border rounded-lg bg-[var(--background-paper)]"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  This response will be visible to customers viewing reviews.
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setReplyModal(null)}>
                  Cancel
                </Button>
                <Button
                  className="btn-primary flex-1"
                  onClick={handleReplyToReview}
                  disabled={replying}
                >
                  {replying ? 'Saving...' : 'Post Response'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Stats */}
      {reviewStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="card p-4 text-center">
            <div className="flex justify-center mb-1">{renderStars(Math.round(reviewStats.average_rating))}</div>
            <p className="text-2xl font-bold text-[var(--primary)]">{reviewStats.average_rating}</p>
            <p className="text-sm text-[var(--text-muted)]">Average Rating</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{reviewStats.total_reviews}</p>
            <p className="text-sm text-[var(--text-muted)]">Total Reviews</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{reviewStats.rating_distribution?.[5] || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">5-Star Reviews</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{pendingReviews.length}</p>
            <p className="text-sm text-[var(--text-muted)]">Pending Approval</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold">
              {reviewStats.total_reviews > 0 ? Math.round((reviewStats.rating_distribution?.[5] || 0) / reviewStats.total_reviews * 100) : 0}%
            </p>
            <p className="text-sm text-[var(--text-muted)]">Satisfaction</p>
          </div>
        </div>
      )}

      {feedbackLoading ? (
        <div className="card p-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)] mx-auto mb-4" />
          <p className="text-[var(--text-muted)]">Loading feedback...</p>
        </div>
      ) : (
        <>
          {/* Pending Reviews */}
          {pendingReviews.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Pending Approval ({pendingReviews.length})
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingReviews.map(review => (
                  <div key={review.id} className="card p-4 border-l-4 border-amber-500" data-testid={`pending-review-${review.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold">{review.customer_name}</p>
                        <p className="text-sm text-[var(--text-muted)]">{review.service_name}</p>
                      </div>
                      <div className="flex">{renderStars(review.rating)}</div>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-[var(--text-secondary)] mb-3 line-clamp-3">
                        &ldquo;{review.comment}&rdquo;
                      </p>
                    )}
                    <p className="text-xs text-[var(--text-muted)] mb-3">
                      {new Date(review.created_at).toLocaleDateString()}
                      {review.worker_name && ` • Service by ${review.worker_name}`}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleApproveReview(review.id)} 
                        className="btn-primary flex-1"
                        data-testid={`approve-review-${review.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => handleApproveReview(review.id, true)} 
                        className="btn-secondary" 
                        title="Approve & Feature"
                        data-testid={`feature-review-${review.id}`}
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleRejectReview(review.id)} 
                        className="text-[var(--error)]"
                        data-testid={`reject-review-${review.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approved Reviews */}
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[var(--success)]" />
              Published Reviews ({approvedReviews.length})
            </h2>
            {approvedReviews.length === 0 && pendingReviews.length === 0 ? (
              <div className="card p-12 text-center">
                <Star className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">No Customer Feedback Yet</h3>
                <p className="text-[var(--text-secondary)]">
                  When customers leave reviews after their service, they&apos;ll appear here for your approval.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {approvedReviews.map(review => (
                  <div 
                    key={review.id} 
                    className={`card p-4 ${review.is_featured ? 'border-2 border-yellow-400' : ''}`}
                    data-testid={`approved-review-${review.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold">{review.customer_name}</p>
                        <p className="text-sm text-[var(--text-muted)]">
                          {review.service_type === 'rides' ? 'Rides' : review.service_type === 'courier' ? 'Courier' : review.service_type === 'laundry' ? 'Laundry' : review.service_type === 'errands' ? 'Errands' : review.service_type}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {review.is_featured && <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />}
                        <div className="flex">{renderStars(review.rating)}</div>
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-[var(--text-secondary)] mb-3">
                        &ldquo;{review.comment}&rdquo;
                      </p>
                    )}
                    
                    {/* Owner Response */}
                    {review.owner_response && (
                      <div className="bg-[var(--primary)]/10 rounded-lg p-3 mb-3 border-l-4 border-[var(--primary)]">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-semibold text-[var(--primary)]">Owner Response</p>
                          <div className="flex gap-1">
                            <button
                              onClick={() => {
                                setReplyModal(review);
                                setReplyText(review.owner_response);
                              }}
                              className="p-1 text-gray-400 hover:text-[var(--primary)]"
                              title="Edit response"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteResponse(review.id)}
                              className="p-1 text-gray-400 hover:text-[var(--error)]"
                              title="Delete response"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)]">{review.owner_response}</p>
                        {review.owner_response_at && (
                          <p className="text-xs text-[var(--text-muted)] mt-1">
                            {new Date(review.owner_response_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <p className="text-xs text-[var(--text-muted)]">
                        {new Date(review.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex items-center gap-1">
                        {/* Reply Button */}
                        {!review.owner_response && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setReplyModal(review);
                              setReplyText('');
                            }}
                            className="text-[var(--primary)] hover:text-[var(--primary)]"
                            title="Reply to review"
                            data-testid={`reply-review-${review.id}`}
                          >
                            <Reply className="w-4 h-4" />
                          </Button>
                        )}
                        {review.worker_name && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleNotifyWorker(review)}
                            className="text-[#D4AF37] hover:text-[#D4AF37]"
                            title={`Notify ${review.worker_name}`}
                            data-testid={`notify-worker-${review.id}`}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleToggleReviewFeatured(review.id)}
                          className={review.is_featured ? 'text-yellow-500' : 'text-gray-400'}
                          title={review.is_featured ? 'Unfeature' : 'Feature'}
                          data-testid={`toggle-featured-${review.id}`}
                        >
                          <Star className={`w-4 h-4 ${review.is_featured ? 'fill-current' : ''}`} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleArchiveReview(review.id)}
                          className="text-gray-400 hover:text-amber-600"
                          title="Archive review"
                          data-testid={`archive-review-${review.id}`}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDeleteReview(review.id)}
                          className="text-[var(--error)] hover:text-[var(--error)]"
                          title="Delete review"
                          data-testid={`delete-review-${review.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
};

export default FeedbackTab;
